import { Box } from '@chakra-ui/react';
import { MaterialIcon } from './MaterialIcon';
import { Tooltip } from './Tooltip';

interface RestrictedBadgeProps {
  /**
   * `'PRIVATE'` reads as "only you", anything else as "only specific people".
   * List surfaces that only know a row is restricted (not which mode) can omit
   * this and get the RESTRICTED wording.
   */
  readonly mode?: 'RESTRICTED' | 'PRIVATE';
  /**
   * Sized via `fontSize`, NOT `boxSize`: `MaterialIcon` writes inline
   * `width/height: 1em`, which outscores Chakra's class-based `boxSize`, so a
   * `boxSize` here would be silently dropped and the glyph would inherit the
   * surface's font-size instead — the exact size drift this component exists to
   * remove. Same reason `RAIL_ICON_SIZE` / `MENU_ICON_SIZE` use `fontSize`.
   */
  readonly fontSize?: string;
}

/**
 * Lock badge for an entity the caller CAN see but that is restricted to
 * specific people. Its presence never means "you lack access" — list and tree
 * queries have already dropped rows the caller can't read, so a badge always
 * sits on something visible.
 *
 * Centralised because the copies had drifted: the drawer list said 13px /
 * "Restricted" while the tree said 14px / "Restricted access", so screen
 * readers announced the same badge by two different names depending on the
 * surface. Now used by the tree row, the drawer list, and the Collections /
 * Artifacts list pages.
 *
 * NOT used by `NavigationBreadcrumb` (two sites): those need
 * `placement="bottom"` and already resolve their own PRIVATE/RESTRICTED
 * wording. They agree with this component's labels — worth folding in if this
 * grows a `placement` prop, but they were never part of the drift.
 */
export function RestrictedBadge({ mode = 'RESTRICTED', fontSize = '14px' }: RestrictedBadgeProps) {
  const label = mode === 'PRIVATE' ? 'Private to you' : 'Restricted access';
  return (
    <Tooltip label={label} placement="right" hasArrow openDelay={300}>
      {/* Chakra's Tooltip needs a DOM child it can attach a ref to. */}
      <Box as="span" flexShrink={0} display="flex" alignItems="center">
        <MaterialIcon name="lock" fontSize={fontSize} color="text.subtle" aria-label={label} />
      </Box>
    </Tooltip>
  );
}
